import { Icon } from "@chakra-ui/react";
import {
  EmrCar,
  EmrClinicalImpression,
  EmrCrossCase,
  EmrForm,
  EmrHeartBolt,
  EmrHpi,
  EmrLabs,
  EmrMdm,
  EmrMedicalHX,
  EmrPerson,
  EmrProcedures,
  EmrRadiology,
  EmrRos,
  EmrSignature,
  EmrTakeVitals,
} from "@medstonetech/slate-icons";
import { ProviderChartCode } from "enums";
import {
  Chart,
  Inbox,
  InfoRoundOutline,
  MedicalCross,
  PillsBottle,
} from "icons";
import {
  ChartNavigationButton,
  ChartNavigationButtonProps,
  ChartsSidebar,
  useChartSidebarIndicators,
} from "modules/charts-shared";
import * as React from "react";
import { useParams } from "react-router-dom";
import { SidebarNavigation } from "shared";
import { ProviderRouteBaseParams } from "../../types/provider-routes";
import { usePermissions } from "contexts";

type ProviderSidebarProps = {
  providerChartCode: ProviderChartCode;
  hiddentSections?: string[] | undefined;
};

type GetNavigationItemsParams = {
  baseUrl: string;
  encounterId: string;
  chartCode: string;
};

function useGetNavigationItems(
  params: GetNavigationItemsParams
): ChartNavigationButtonProps[] {
  const { baseUrl, ...restParams } = params;
  const { scope } = usePermissions();

  const { data } = useChartSidebarIndicators({
    encounterId: restParams.encounterId,
    chartCode: restParams.chartCode,
  });

  return [
    {
      label: "Patient Info",
      sectionId: "SPIF",
      labelIcon: <Icon as={InfoRoundOutline} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/patient-info`,
      notesType: "shared",
      isAccessible:
        scope("provider:patientinfo").isAccessible ||
        scope("audits").isAccessible,
      isSaved: true,
      ...restParams,
    },
    {
      label: "Arrival Info",
      sectionId: "SARI",
      labelIcon: <Icon as={EmrCar} color="blue" fontSize="1.25rem" />,
      hasAudits: true,
      isShared: true,
      to: `${baseUrl}/arrival-info`,
      notesType: "shared",
      isAccessible:
        scope("nurse:ai").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isArrivalInfoSaved ?? false,
      isLastSaved: data?.data.lastModified === "SARI",
      ...restParams,
    },
    {
      label: "Medical HX",
      sectionId: "SMHX",
      hasAudits: true,
      labelIcon: <Icon as={EmrMedicalHX} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/medical-hx`,
      notesType: "shared",
      isAccessible:
        scope("provider:mh").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isMedicalHistorySaved ?? false,
      isLastSaved: data?.data.lastModified === "SMHX",
      ...restParams,
    },
    {
      label: "Vitals",
      sectionId: "vitals",
      labelIcon: <Icon as={EmrTakeVitals} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/vitals`,
      notesType: "shared",
      isAccessible:
        scope("provider:vitals").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isVitalsSaved ?? false,
      isLastSaved: data?.data.lastModified === "Vitals",
      ...restParams,
    },
    {
      label: "Order History",
      sectionId: "order-history",
      labelIcon: <Icon as={Inbox} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/order-history`,
      notesType: "shared",
      isAccessible:
        scope("provider:odh").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isOrderHistorySaved ?? false,
      hasPendingOrdersCancellation: data?.data?.hasPendingOrdersCancellation,
      ...restParams,
    },
    {
      label: "HPI",
      sectionId: "SHPI",
      hasAudits: true,
      labelIcon: <Icon as={EmrHpi} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/hpi`,
      notesType: "general",
      isAccessible:
        scope("provider:hpi").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isHPISaved ?? false,
      isLastSaved: data?.data.lastModified === "SHPI",
      ...restParams,
    },
    {
      label: "ROS",
      sectionId: "SROS",
      hasAudits: true,
      labelIcon: <Icon as={EmrRos} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/ros`,
      notesType: "general",
      isAccessible:
        scope("provider:ros").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isROSSaved ?? false,
      isLastSaved: data?.data.lastModified === "SROS",
      ...restParams,
    },
    {
      label: "Physical Exam",
      sectionId: "SPEX",
      hasAudits: true,
      labelIcon: <Icon as={EmrPerson} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/physical-exam`,
      notesType: "general",
      hasDiagrams: true,
      isAccessible:
        scope("provider:pex").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isPhysicalExamSaved ?? false,
      isLastSaved: data?.data.lastModified === "SPEX",
      ...restParams,
    },
    {
      label: "Orders",
      sectionId: "SPOF",
      labelIcon: <Icon as={EmrForm} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/order-form`,
      notesType: "shared",
      isShared: true,
      isAccessible:
        scope("provider:odf").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isOrderFormSaved ?? false,
      isLastSaved: data?.data.lastModified === "SPOF",
      ...restParams,
    },
    {
      label: "Meds & IV",
      sectionId: "SIVM",
      labelIcon: <Icon as={PillsBottle} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/iv-and-medications`,
      notesType: "shared",
      isAccessible:
        scope("provider:ivm").isAccessible || scope("audits").isAccessible,
      showIvMedicationCounters: true,
      isSaved: data?.data.isIvMedicationSaved ?? false,
      isLastSaved: data?.data.lastModified === "SIVM",
      ...restParams,
    },
    {
      label: "Treatments",
      sectionId: "SIVM",
      labelIcon: <Icon as={MedicalCross} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/treatments`,
      notesType: "shared",
      isAccessible:
        scope("provider:treatments").isAccessible ||
        scope("audits").isAccessible,
      showTreatmentsCounters: true,
      isSaved: data?.data.isIvMedicationSaved ?? false,
      isLastSaved: data?.data.lastModified === "SIVM",
      ...restParams,
    },
    {
      label: "Radiology",
      sectionId: "SRAD",
      hasAudits: true,
      notesType: "order",
      orderType: "Radiology",
      labelIcon: <Icon as={EmrRadiology} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/radiology`,
      isAccessible:
        scope("provider:rad").isAccessible || scope("audits").isAccessible,
      showOrderCounters: "Radiology",
      isSaved: data?.data.isRadiologySaved ?? false,
      isLastSaved: data?.data.lastModified === "SRAD",
      ...restParams,
    },
    {
      label: "Labs",
      sectionId: "SLAB",
      hasAudits: true,
      notesType: "order",
      orderType: "Labs",
      labelIcon: <Icon as={EmrLabs} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/labs`,
      isAccessible:
        scope("provider:labs").isAccessible || scope("audits").isAccessible,
      showOrderCounters: "Lab",
      isSaved: data?.data.isLabsSaved ?? false,
      isLastSaved: data?.data.lastModified === "SLAB",
      ...restParams,
    },
    {
      label: "EKG",
      sectionId: "SEKG",
      hasAudits: true,
      labelIcon: <Icon as={EmrHeartBolt} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/ekg`,
      notesType: "general",
      isAccessible:
        scope("provider:ekg").isAccessible || scope("audits").isAccessible,
      showEKGCounters: true,
      isSaved: data?.data.isEKGSaved ?? false,
      isLastSaved: data?.data.lastModified === "SEKG",
      ...restParams,
    },
    {
      label: "Procedures",
      sectionId: "SPRO2",
      hasAudits: true,
      labelIcon: <Icon as={EmrProcedures} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/procedures`,
      notesType: "general",
      isAccessible:
        scope("provider:proc").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isProceduresSaved ?? false,
      isLastSaved: data?.data.lastModified === "SPRO2",
      ...restParams,
    },
    {
      label: "Progress",
      sectionId: "SPRG",
      labelIcon: <Icon as={Chart} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/progress`,
      notesType: "shared",
      isAccessible:
        scope("provider:prog").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isProgressSaved ?? false,
      isLastSaved: data?.data.lastModified === "SPRG",
      ...restParams,
    },
    {
      label: "Procedural Sedation Note",
      sectionId: "SPSN",
      hasAudits: true,
      labelIcon: <Icon as={EmrProcedures} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/procedural-sedation-note`,
      notesType: "shared",
      isShared: true,
      isAccessible:
        scope("provider:notes").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isProceduralSedationNotesSaved ?? false,
      isLastSaved: data?.data.lastModified === "SPSN",
      ...restParams,
    },
    {
      label: "Supplies",
      sectionId: "SSUP",
      labelIcon: <Icon as={EmrCrossCase} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/supplies`,
      notesType: "shared",
      isShared: true,
      isAccessible:
        scope("provider:sup").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isSuppliesSaved ?? false,
      isLastSaved: data?.data.lastModified === "SSUP",
      ...restParams,
    },
    {
      label: "MDM",
      sectionId: "SMDM",
      hasAudits: true,
      labelIcon: <Icon as={EmrMdm} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/mdm`,
      notesType: "shared",
      isShared: true,
      isAccessible:
        scope("provider:mdm").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isMDMSaved ?? false,
      isLastSaved: data?.data.lastModified === "SMDM",
      ...restParams,
    },
    {
      label: "Clinical Impression",
      sectionId: "SCLI",
      hasAudits: true,
      labelIcon: (
        <Icon as={EmrClinicalImpression} color="blue" fontSize="1.25rem" />
      ),
      to: `${baseUrl}/clinical-impression`,
      notesType: "general",
      isAccessible:
        scope("provider:ci").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isClinicalImpressionSaved ?? false,
      isLastSaved: data?.data.lastModified === "SCLI",
      ...restParams,
    },
    {
      label: "Disposition",
      sectionId: "SDIS",
      hasAudits: true,
      labelIcon: <Icon as={EmrSignature} color="blue" fontSize="1.25rem" />,
      to: `${baseUrl}/disposition`,
      notesType: "shared",
      isAccessible:
        scope("provider:disp").isAccessible || scope("audits").isAccessible,
      isSaved: data?.data.isDispositionSaved ?? false,
      isLastSaved: data?.data.lastModified === "SDIS",
      showMOT: true,
      ...restParams,
    },
  ];
}

function ProviderSidebar(props: ProviderSidebarProps) {
  const { providerChartCode, hiddentSections = ["SARI", "SSUP", "SPSN"] } =
    props;
  const { encounterId = "" } = useParams<ProviderRouteBaseParams>();
  const mainRoute = window.location.hash.split("/")[1];
  const NAVIGATION_ITEMS = useGetNavigationItems({
    baseUrl: `/${mainRoute}/charts/${encounterId}/chart/${providerChartCode}`,
    chartCode: providerChartCode,
    encounterId,
  });

  return (
    <ChartsSidebar>
      <SidebarNavigation>
        {NAVIGATION_ITEMS.filter(
          (item) =>
            item.isAccessible && !hiddentSections.includes(item.sectionId)
        ).map((navItem) => (
          <ChartNavigationButton key={navItem.sectionId} {...navItem} />
        ))}
      </SidebarNavigation>
    </ChartsSidebar>
  );
}

export { ProviderSidebar };
